import React, { useEffect, useState } from 'react';
import { ChevronDown, HelpCircle, MessageCircleQuestion, ShieldCheck, ThumbsUp } from 'lucide-react';
import { questionsAPI } from '../services/api';
import { useT } from '../services/i18n';

export const openQuestionForm = () => {
  document.getElementById('questions')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  window.dispatchEvent(new Event('open-question-form'));
};

const ProductQuickFaq = ({ productId }) => {
  const { t } = useT();
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    setItems(null);
    setOpen(null);
    questionsAPI.forProduct(productId).then(d => {
      const answered = d.questions.filter(q => q.status === 'answered');
      setTotal(answered.length);
      setItems([...answered].sort((a, b) => (b.helpful_count || 0) - (a.helpful_count || 0) || new Date(b.created_at) - new Date(a.created_at)).slice(0, 3));
    }).catch(() => setItems([]));
  }, [productId]);

  if (!items) return null;

  if (items.length === 0) {
    return (
      <div className="mt-5 flex items-center gap-3 border border-dashed border-gray-300 rounded-lg px-4 py-3" data-testid="quick-faq-empty">
        <HelpCircle size={20} className="text-orange-500 flex-shrink-0" />
        <p className="text-sm text-gray-700 flex-1">{t('faq.askPrompt')}</p>
        <button type="button" onClick={openQuestionForm} data-testid="quick-faq-ask" className="text-sm font-medium text-orange-600 hover:text-orange-700 hover:underline whitespace-nowrap">{t('faq.ask')}</button>
      </div>
    );
  }

  return (
    <div className="mt-5 border border-gray-200 rounded-lg overflow-hidden" data-testid="quick-faq">
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
        <p className="text-sm font-semibold text-gray-900 flex items-center gap-2"><MessageCircleQuestion size={16} className="text-orange-500" /> {t('faq.title')}</p>
        <a href="#questions" onClick={(e) => { e.preventDefault(); document.getElementById('questions')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} data-testid="quick-faq-see-all" className="text-xs text-blue-600 hover:underline">{t('faq.seeAll', { n: total })}</a>
      </div>
      <ul className="divide-y divide-gray-100">
        {items.map((q, i) => (
          <li key={q.id} data-testid={`quick-faq-item-${i}`}>
            <button type="button" onClick={() => setOpen(open === q.id ? null : q.id)} aria-expanded={open === q.id} data-testid={`quick-faq-toggle-${i}`}
              className="w-full flex items-start gap-2 px-4 py-2.5 text-left text-sm text-gray-800 hover:bg-orange-50/50 transition-colors">
              <span className="flex-1">{q.question}</span>
              {q.helpful_count > 0 && <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 mt-0.5"><ThumbsUp size={11} /> {q.helpful_count}</span>}
              <ChevronDown size={16} className={`text-gray-400 mt-0.5 flex-shrink-0 transition-transform ${open === q.id ? 'rotate-180' : ''}`} />
            </button>
            {open === q.id && (
              <div className="px-4 pb-3 -mt-1" data-testid={`quick-faq-answer-${i}`}>
                <div className="bg-orange-50/70 border-l-4 border-orange-400 rounded-r-lg p-3">
                  <p className="text-xs font-semibold text-gray-900 flex items-center gap-1.5"><ShieldCheck size={12} className="text-orange-500" /> {t('faq.answerFrom', { author: q.answer.author })}</p>
                  <p className="text-sm text-gray-700 mt-1 whitespace-pre-line">{q.answer.message}</p>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ProductQuickFaq;
